import { useEffect } from 'react';
import { Box, Container, Typography } from '@mui/material';
import ChecklistIcon from '@mui/icons-material/Checklist';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import ListAltIcon from '@mui/icons-material/ListAlt';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import { useLocation } from 'react-router-dom';
import Footer from '@/components/common/Footer';
import Navbar from '@/components/common/Navbar';
import FootprintCarousel from '@/components/common/FootprintCarousel';
import digitalNetworkBanner from '@/assets/digital-network-banner.png';
import { FOOTPRINTS } from '@/config/footprints';

export default function AllFootprintsPage() {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) {
      return;
    }

    const sectionId = location.hash.replace('#', '');
    const sectionElement = document.getElementById(sectionId);

    if (sectionElement) {
      // Delay ensures the page is fully painted before smooth-scrolling.
      requestAnimationFrame(() => {
        sectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [location.hash]);

  return (
    <Box id="landing-top" sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#f8f9fa' }}>
      <Navbar />

      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
          color: '#ffffff',
          backgroundColor: '#0b1e3a',
          backgroundImage: `linear-gradient(120deg, rgba(11,30,58,0.88) 0%, rgba(10,58,92,0.55) 55%, rgba(11,30,58,0.88) 100%), url(${digitalNetworkBanner})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 10px 30px rgba(11, 30, 58, 0.25)',
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            position: 'relative',
            zIndex: 1,
            minHeight: { xs: 300, md: 420 },
            display: 'flex',
            alignItems: 'center',
            py: { xs: 5, md: 8 },
          }}
        >
          <Box sx={{ maxWidth: 820 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                mb: 2,
                color: '#ffffff',
                textShadow: '0 2px 12px rgba(0,0,0,0.35)',
                fontSize: { xs: '1.9rem', sm: '2.25rem', md: '2.6rem' },
                lineHeight: 1.2,
              }}
            >
              Who shapes global conversations online?
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 2.5, color: 'rgba(255,255,255,0.92)', textShadow: '0 1px 8px rgba(0,0,0,0.3)' }}
            >
              Track how organisations and media appear in search results across diplomacy, technology, and AI topics.
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: 'rgba(255,255,255,0.88)', textShadow: '0 1px 8px rgba(0,0,0,0.3)' }}
            >
              <Box
                component="span"
                sx={{ color: '#ffa200', fontWeight: 700 }}
              >
                The Digital Footprint
              </Box>{' '}
              platform measures online visibility of Geneva-based organisations across key diplomatic domains and fields. 
            </Typography>
          </Box>
        </Container>
      </Box>

      <Box
        id="explore"
        sx={{
          width: '100%',
          backgroundColor: '#ffffff',
          border: 'none',
          pt: { xs: 8, md: 14 },
          pb: { xs: 4, md: 7 },
          scrollMarginTop: 80,
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#1f1f1f', fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
            Explore the Digital Footprint
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: '#3c4043' }}>
            Choose a lens to analyse global visibility:
          </Typography>

          <Box sx={{ mb: 4 }}>
            <FootprintCarousel items={FOOTPRINTS} />
          </Box>

          <Typography variant="body1" sx={{ mt: 2, color: '#3c4043' }}>
            The apps differ by topic (diplomacy or technology) and actor (organisations, media, or AI ecosystem),
            using a shared methodology.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ flexGrow: 1, py: 5 }}>
        <Typography
          id="why-visibility-matters"
          variant="h4"
          sx={{
            fontWeight: 700,
            mb: 1.5,
            color: '#1f1f1f',
            scrollMarginTop: 96,
            fontSize: { xs: '1.75rem', md: '2.125rem' },
          }}
        >
          Why visibility matters:
        </Typography>
        <Typography variant="body1" sx={{ mb: 1.25, color: '#3c4043' }}>
          For most people, search is the first doorway into a topic. Whoever appears on the first page quietly sets
          the frame for what a debate is about and whose voice is taken seriously.
        </Typography>
        <Typography variant="body1" sx={{ mb: 1.25, color: '#3c4043' }}>
          Visibility builds familiarity, and familiarity builds trust. Over time, the same few actors become the
          default reference points, while smaller or non-Western voices risk becoming invisible.
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, color: '#3c4043' }}>
          Measuring this digital footprint shows who is shaping online narratives around global policy, exposes gaps
          in representation, and helps organisations see where they stand.
        </Typography>
      </Container>

      <Box
        id="how-it-works"
        sx={{
          width: '100%',
          backgroundColor: '#ffffff',
          py: { xs: 6, md: 10 },
          scrollMarginTop: 96,
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1.5, color: '#1f1f1f', fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
            How it works:
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: '#3c4043' }}>
            In simple terms, we measure what the internet shows first.
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gap: 2.5,
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            }}
          >
            {[
              {
                icon: ChecklistIcon,
                title: 'Pick the topics',
                description:
                  'For each domain (diplomacy, tech, AI), we choose a list of topics people actually search for.',
              },
              {
                icon: TravelExploreIcon,
                title: 'Run the searches',
                description:
                  'We run Google searches from more than 50 cities worldwide, just like a normal user would.',
              },
              {
                icon: ListAltIcon,
                title: 'Record who appears',
                description:
                  'For every search, we log which organisations and media show up and at which position on the page.',
              },
              {
                icon: LeaderboardIcon,
                title: 'Turn it into rankings',
                description:
                  'The same method is used for all apps, so results can be compared across topics, actors, and regions.',
              },
            ].map((item) => {
              const StepIcon = item.icon;
              return (
              <Box
                key={item.title}
                sx={{
                  position: 'relative',
                  p: 2.5,
                  borderRadius: 3,
                  border: '1px solid #e5e7eb',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
                }}
              >
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    backgroundColor: '#ffa200',
                    color: '#ffffff',
                    mb: 1.5,
                    boxShadow: '0 2px 6px rgba(255, 162, 0, 0.35)',
                  }}
                >
                  <StepIcon sx={{ fontSize: 24 }} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1f1f1f', mb: 0.5 }}>
                  {item.title}
                </Typography>
                <Typography variant="body2" sx={{ color: '#3c4043' }}>
                  {item.description}
                </Typography>
              </Box>
              );
            })}
          </Box>
        </Container>
      </Box>

      <Box
        id="methodology"
        sx={{
          width: '100%',
          backgroundColor: '#f8f9fa',
          py: { xs: 6, md: 10 },
          scrollMarginTop: 96,
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1.5, color: '#1f1f1f', fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
            Methodology
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: '#3c4043' }}>
          A single, transparent method is applied across all apps to ensure results remain comparable.
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gap: 2.5,
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              mb: 3,
            }}
          >
            {[
              {
                title: 'Shared search engine',
                description:
                  'All data comes from Google Search and AI Overviews, the most widely used doorway to online information worldwide.',
              },
              {
                title: 'Curated topic lists',
                description:
                  'For each domain (diplomacy, tech, AI), we maintain a list of relevant topics, reviewed and updated by experts.',
              },
              {
                title: 'Multi-city searches',
                description:
                  'Each topic is searched across 50 cities worldwide to capture how results vary by geography.',
              },
              {
                title: 'Position-based scoring',
                description:
                  'Higher-ranked results count more. Each actor gets a score based on where they appear on the Google search or AI overview.',
              },
              {
                title: 'Actor classification',
                description:
                  'Domains are mapped to the actors behind them, organisations, media outlets, or AI-ecosystem players, so visibility can be compared fairly.',
              },
              {
                title: 'Regular updates',
                description:
                  'Searches are re-run on a regular cadence, so the footprint reflects how the online landscape changes over time.',
              },
            ].map((item) => (
              <Box
                key={item.title}
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  border: '1px solid #e5e7eb',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1f1f1f', mb: 0.5 }}>
                  {item.title}
                </Typography>
                <Typography variant="body2" sx={{ color: '#3c4043' }}>
                  {item.description}
                </Typography>
              </Box>
            ))}
          </Box>

          <Typography variant="body2" sx={{ color: '#5f6368', fontStyle: 'italic' }}>
          Because all apps follow the same steps, the Digital Footprint can be meaningfully compared across topics, actors, and regions.
          </Typography>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
